import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MAX_PITCH, MIN_PITCH, SCENE_VIEW, clamp } from '../engine/battlefield/projection';
import { beatPairOf, pairFor, shotForPair, shotKind } from '../engine/display/turnShot';
import { SLOP_PX } from './usePingGesture';
import { BREATH_FOR_MS, breathe, cameraAt, planMove } from '../engine/display/cameraMotion';
import { approach } from '../engine/display/timeline';
import useDisplayClock from './useDisplayClock';

/** A resting camera redraws at most this often: the drift is slow, and every redraw is the whole map. */
const BREATH_FRAME_MS = 80;
/**
 * Requests this close together are one move: a turn ending moves the order,
 * then the seat follows it to the next actor and the target a beat later -
 * three changes, one turn, ONE move (the review of Track C).
 */
const SETTLE_MS = 120;
/** The share of the gap to a walking figure closed per FRAME_MS: the camera pans after it, never ahead of it. */
const PAN_K = 0.15;
/** The pan's reference frame: the share above is per sixtieth of a second, whatever the screen's rate. */
const FRAME_MS = 1000 / 60;
const NO_SOLIDS = [];

/**
 * useDisplayCamera - the display's camera follows the action (plan
 * map-display, Track C; the camera rules of 2026-10-07).
 *
 * ONE MOVE PER TURN: the order moving on, or this viewer's own pick (counted
 * for the GM, or in the viewer's own turn), gathered over SETTLE_MS into one
 * request; the pair it comes to (`engine/display/turnShot`) gets one move to
 * its shot, then the camera rests, breathing. A figure this screen has no
 * marker for (hidden from it) gets no move. It drives the panel's own camera,
 * so every gesture, pick and measurement reads what is on the screen.
 *
 * A WALKING FIGURE (plan design-gaps, M3) pulls a following camera along: each
 * frame pans the target toward the walker, yaw and zoom untouched - a pan is
 * not a shot, so one move per turn holds; a move in progress wins over it.
 * The walk over, the pan finishes onto where the figure stopped (2026-10-17:
 * an eased pan trails a moving figure, and stopping with the walk left each
 * screen's camera short of it by its own frame rate's amount), and the
 * breathing settles round there. A wheel-taken camera does not follow.
 *
 * TAKING THE WHEEL: any change to the camera that is not the follow's own - an
 * orbit, a pan, a zoom, a preset, Reset, Build's top-down view, a jump to a
 * marker - stops the following where it is. `follow()` gives the wheel back;
 * a pitch the hand chose (not a shot's) becomes the viewer's own, the one every
 * two-shot starts from. The GM's cut moves any display camera, once.
 *
 * @param {object} o
 * @param {boolean} o.enabled - the display is the renderer
 * @param {object} o.cam - the panel's camera (useBattlefieldCamera): read through `getCamera` when a move or a
 *   frame needs it, never as rendered - a camera frame re-renders nothing here (hooks/cameraStore)
 * @param {Record<string, object>} o.tokens - the markers by id, as the panel holds them
 * @param {string|null} o.actorId - this screen's acting-as
 * @param {string|null} o.targetId - this screen's target
 * @param {string|null} o.turnActorId - whose turn it is in the order, or null
 * @param {boolean} [o.isGm] - the GM's seat: its picks always count
 * @param {boolean} o.holdsStill - the viewer's motion setting: moves are instant, nothing breathes
 * @param {boolean} [o.shoulder] - the viewer's "Over the shoulder"
 * @param {boolean} [o.breathing] - the viewer's breathing drift (off unless asked for: the perf overhaul)
 * @param {number} [o.lens] - the field of view every two-shot takes, or none (flat)
 * @param {boolean} [o.follow] - the viewer's "The camera follows the action" (on unless said)
 * @param {{id: string, seq: number}|null} [o.cut] - the GM's newest cut (useCameraCut)
 * @param {boolean} [o.xray] - the viewer's "never lose the actor": a two-shot swings round terrain
 * @param {object[]} [o.solids] - the terrain, for that swing
 * @param {{id: string, x: number, z: number}|null} [o.walking] - the figure walking now, where it is this frame
 *   (useDisplayWalks); null when nobody walks
 * @returns {{ following: boolean, wheelTaken: boolean, follow: () => void, cam: object,
 *   pair: {actorId: string|null, targetId: string|null}|null, shot: string|null }} - `shot` is the camera label's
 *   words (Z6); `cam` is the panel's camera, its
 *   gestures taking the wheel; `wheelTaken` is when "Follow the action" is offered; `pair` is who the
 *   camera last framed, which the focus, the fade and the x-ray follow
 */
export default function useDisplayCamera(o) {
  // `ours`: every camera the follow itself set - anything else on screen was somebody else's hand.
  const live = useRef({ move: null, settledAt: 0, drawnAt: 0, pannedAt: 0, ours: new WeakSet() });
  // The latest props, for the moves that start later (a settled request, a cut) to read.
  const latest = useRef(o);
  useLayoutEffect(() => {
    latest.current = o;
  });
  const state = useFollowState(latest, live);
  const canFollow = o.enabled && (o.follow ?? true);
  const following = canFollow && state.following;
  const go = useMove(latest, live, state);
  useShotPlanner(o, latest, following, go, state.setPair);
  useAttackBeat(o, latest, following, go, state);
  useFollowBack(o.turnActorId, canFollow && !state.following, state.follow);
  useCut(o, go);
  useFollowClock(o, following, live, state);
  // The panel's camera is a new object every render, so there is nothing to memoise here.
  const press = useRef(null);
  const cam = takingTheWheel(o.cam, following, state.takeWheel, press);
  const wheelTaken = canFollow && !state.following;
  return {
    following,
    wheelTaken,
    follow: state.follow,
    cam,
    pair: state.pair,
    shot: shotLabel(o, wheelTaken, state.kind),
  };
}

/**
 * The camera label's words (plan design-gaps, Z6; the session page's `.camtag`): ESTABLISHING before any shot, the
 * shot's kind after, YOUR CAMERA once the hand has the wheel; none where the camera does not follow at all.
 */
function shotLabel(o, wheelTaken, kind) {
  if (!o.enabled || !(o.follow ?? true)) return null;
  if (wheelTaken) return 'YOUR CAMERA';
  return kind ?? 'ESTABLISHING';
}

/** Following or not, moving or resting, the viewer's own pitch (`homePitch`), and who was last framed. */
function useFollowState(latest, live) {
  const [following, setFollowing] = useState(true);
  const [phase, setPhase] = useState('idle');
  const [pair, setPair] = useState(null);
  // The kind of the last shot taken (engine/display/turnShot `shotKind`), for the camera label (Z6).
  const [kind, setKind] = useState(null);
  const homePitch = useRef(latest.current.cam.getCamera().pitch);
  // The camera as the follow found it is its own: a walk pans it from there before any shot (the Track M review).
  useEffect(() => void live.current.ours.add(latest.current.cam.getCamera()), [latest, live]);
  const takeWheel = useCallback(() => {
    setFollowing(false);
    setPhase('idle');
  }, []);
  const follow = useCallback(() => {
    // Only a pitch the hand chose is the viewer's: a shot's (over the shoulder's 13, a crane's 58) is not.
    const { pitch } = latest.current.cam.getCamera();
    if (pitch !== live.current.move?.to.pitch) homePitch.current = clamp(pitch, MIN_PITCH, MAX_PITCH);
    live.current.ours.add(latest.current.cam.getCamera());
    setFollowing(true);
  }, [latest, live]);
  return { following, phase, setPhase, pair, setPair, kind, setKind, homePitch, takeWheel, follow };
}

/** Does this screen have a marker for it - the turn shots' and the beat's test. */
const placedOn = (tokens) => (id) => !!id && !!tokens?.[id];

/** This screen's side of a turn, as the turn shots read it. */
const seatOf = (o) => ({ actorId: o.actorId, targetId: o.targetId, turnActorId: o.turnActorId, isGm: !!o.isGm });

/** The one way a move starts: the shot for a pair, from where the camera is, then the clock. */
function useMove(latest, live, { homePitch, setPhase, setPair, setKind }) {
  return useCallback(
    (pair, shoulder) => {
      const o = latest.current;
      const context = {
        seat: seatOf(o),
        tokens: o.tokens,
        home: { pitch: homePitch.current, fov: o.lens },
        shoulder,
        xray: !!o.xray,
        solids: o.solids ?? NO_SOLIDS,
        view: SCENE_VIEW,
      };
      const camera = o.cam.getCamera();
      const shot = shotForPair(pair, camera, context);
      if (!shot) return;
      setKind(shotKind(pair, context));
      // The follow (or a GM's cut) takes the camera: a hand's glide still running would fight it (T5).
      o.cam.dropGlide?.();
      live.current.move = planMove(camera, shot, window.performance.now(), o.holdsStill);
      live.current.ours.add(camera);
      setPair(pair);
      setPhase('moving');
    },
    [latest, live, homePitch, setPhase, setPair, setKind],
  );
}

/**
 * One move per turn: requests (the order moving on, this viewer's pick, a new
 * lens or a Follow) gathered over SETTLE_MS, then one move to the pair they
 * come to (`pairFor`).
 */
function useShotPlanner(o, latest, following, go, setPair) {
  const asked = useRef(new Set());
  const timer = useRef(null);
  const followingNow = useRef(following);
  useLayoutEffect(() => {
    followingNow.current = following;
  });
  const ask = useCallback(
    (kind) => {
      asked.current.add(kind);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        const now = latest.current;
        const pair = pairFor(asked.current, seatOf(now), placedOn(now.tokens));
        asked.current = new Set();
        if (!pair) return;
        // The action moves on even with the wheel in the viewer's hand (the focus follows it); the camera, only when following.
        if (followingNow.current) go(pair, now.shoulder ?? false);
        else setPair(pair);
      }, SETTLE_MS);
    },
    [latest, go, setPair],
  );
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const { turnActorId, actorId, targetId, lens } = o;
  useEffect(() => {
    if (turnActorId) ask('order');
  }, [turnActorId, ask]);
  useEffect(() => {
    if (actorId) ask('pick');
  }, [actorId, targetId, ask]);
  // A new lens re-frames (or the resting camera would breathe the old one back); so does a Follow.
  useEffect(() => {
    if (!following) return;
    ask('pick');
    ask('order');
  }, [following, lens, ask]);
}

/**
 * The GM's cut (useCameraCut): one move to that figure on any display, over
 * this viewer's options - with the follow off, or the wheel taken, it still
 * moves. Each cut moves a screen once: switching to the display later does
 * not replay one it already let pass.
 */
function useCut(o, go) {
  const seen = useRef(null);
  const { enabled, cut } = o;
  useEffect(() => {
    if (!cut || seen.current === cut.seq) return;
    seen.current = cut.seq;
    if (enabled) go({ actorId: cut.id, targetId: null }, false);
  }, [enabled, cut, go]);
}

/**
 * The clock while a move runs, then the breathing (throttled, and for a
 * minute at most), unless the screen holds still. A camera that changed under
 * it - not by its own hand - means somebody took the wheel: it stops there.
 */
function useFollowClock(o, following, live, { phase, setPhase, takeWheel, pair }) {
  const breathes = phase === 'resting' && (o.breathing ?? false) && !o.holdsStill;
  const { walker, arrived } = useWalker(o.walking, pair);
  // A move runs to its end on any display (a cut moves even a camera the viewer holds); only a follower breathes,
  // and only a follower is pulled along by a walking figure - and on to where it stopped.
  const panning = following && !!walker;
  // A pan's pace reads the time since its last frame: a new pan starts afresh.
  useEffect(() => {
    if (!panning) live.current.pannedAt = 0;
  }, [panning, live]);
  useDisplayClock(o.enabled && (phase === 'moving' || (following && breathes) || panning), (now) => {
    const l = live.current;
    if (!l.ours.has(o.cam.getCamera())) return void takeWheel();
    const next = frameOf(l, { ...o, walking: walker }, now, phase, setPhase);
    if (!next) return void arrived();
    l.ours.add(next);
    o.cam.setCamera(next);
  });
}

/**
 * Who pulls the camera: the walking figure, or after its walk where it stopped, until the pan has caught up
 * (`arrived`, called when a frame has nothing left to do; the breathing takes it from there).
 *
 * A NEW SHOT (`shot`, the pair each move frames) ENDS THE PAN (2026-10-28): a turn usually ends right after its actor
 * moves, and the walk - still playing, or its stop not yet reached - pulled the camera back to the actor whose turn
 * had just passed, after the new turn's shot. The figure walking when a shot begins is that shot's past: its walk is
 * not followed again until it ends; a walk after it is.
 */
function useWalker(walking, shot) {
  const [end, setEnd] = useState(null);
  const last = useRef(null);
  const stale = useRef(null);
  useEffect(() => {
    stale.current = last.current?.id ?? null;
    last.current = null;
    setEnd(null);
  }, [shot]);
  const fresh = walking && walking.id !== stale.current ? walking : null;
  useEffect(() => {
    if (!walking) stale.current = null;
  }, [walking]);
  useEffect(() => {
    // Asked again here, not taken from the render: a walk frame landing in the shot's own render saw `stale` before
    // the shot's effect set it, and would have become the end the pan returns to (the review, 2026-10-28).
    if (fresh && fresh.id !== stale.current) {
      last.current = fresh;
      // Only when there is an end to clear: `walking` is a new object every frame of a walk, and an update set
      // from an effect on every frame is what React counts toward "Maximum update depth exceeded" (fuzz
      // 2026-10-17) - an unchanged value is not skipped while the panel has other work pending.
      if (end !== null) setEnd(null);
    } else if (last.current) {
      setEnd(last.current);
      last.current = null;
    }
  }, [fresh, end]);
  const arrived = useCallback(() => setEnd(null), []);
  return { walker: fresh ?? end, arrived };
}

/** The frame: the move to its end, else the pan after the walker, else the breath. */
function frameOf(l, o, now, phase, setPhase) {
  if (phase === 'moving') return moveFrame(l, now, setPhase);
  if (o.walking) return panFrame(l, o.cam.getCamera(), o.walking, now);
  return breathFrame(l, now, setPhase);
}

/**
 * A frame of the pan: the target eased after the walker, yaw and zoom untouched; null once it is there. By the
 * time since the last pan frame, so a 120 Hz screen and a stalling one pan at the same speed (the Track M review).
 */
function panFrame(l, camera, walker, now) {
  const dt = l.pannedAt ? now - l.pannedAt : FRAME_MS;
  l.pannedAt = now;
  const k = 1 - (1 - PAN_K) ** (dt / FRAME_MS);
  const target = { x: approach(camera.target.x, walker.x, k), z: approach(camera.target.z, walker.z, k) };
  if (target.x === camera.target.x && target.z === camera.target.z) return null;
  // The breathing settles round the new spot, not the shot's.
  if (l.move) l.move = { ...l.move, to: { ...l.move.to, target } };
  return { ...camera, target };
}

/** A frame of the move; at its end the camera rests. */
function moveFrame(l, now, setPhase) {
  if (now >= l.move.start + l.move.ms) {
    // The move's own end, not this frame's time: a late render must not push the rest later.
    l.settledAt = l.move.start + l.move.ms;
    setPhase('resting');
  }
  return cameraAt(l.move, now);
}

/** A frame of the breathing, no more often than BREATH_FRAME_MS (null between); after BREATH_FOR_MS, still. */
function breathFrame(l, now, setPhase) {
  if (now - l.settledAt > BREATH_FOR_MS) {
    setPhase('still');
    return null;
  }
  if (now - l.drawnAt < BREATH_FRAME_MS) return null;
  l.drawnAt = now;
  return breathe(l.move.to, now - l.settledAt);
}

/** How long after a turn passes an attack by its actor is taken for one landing late, not a new action. */
const LATE_BEAT_MS = 3000;

/**
 * THE ATTACK'S BEAT (2026-10-21: "the camera doesn't move on actions like attack"): the newest live attack moves the
 * camera to its attacker and target - at most once a turn (once per pair with no order running), never for a
 * critical, a replay or an attacker this screen cannot see (`beatPairOf`). The first impacts a screen sees are
 * history. With the wheel in the viewer's hand the pair is still noted, as a pick's is.
 */
function useAttackBeat(o, latest, following, go, { pair: framed, setPair }) {
  const seen = useRef(null);
  // The keys this turn's beats have used: the turn's own, or (no order running) each pair's. A new turn clears them.
  const used = useRef(new Set());
  const framedRef = useRef(framed);
  framedRef.current = framed;
  const { impacts, enabled, turnActorId } = o;
  // Whose turn it was before this one, and when it passed: their last attack can land on this screen just after the
  // turn passed (2026-10-28) - but only just after; later, theirs is a reaction like anyone's.
  const turns = useRef({ now: turnActorId, late: null });
  useEffect(() => {
    used.current.clear();
    if (turns.current.now === turnActorId) return undefined;
    turns.current = { now: turnActorId, late: turns.current.now };
    const over = window.setTimeout(() => (turns.current = { ...turns.current, late: null }), LATE_BEAT_MS);
    return () => window.clearTimeout(over);
  }, [turnActorId]);
  useEffect(() => {
    const list = Object.values(impacts ?? {});
    const was = seen.current;
    seen.current = new Set(list.map((i) => i.id));
    // The first list is history; and the classic map spends no beat it cannot show.
    if (was === null || !enabled) return;
    const now = latest.current;
    const pair = beatToTake(list, was, now, used.current, framedRef.current, turns.current.late);
    if (!pair) return;
    if (following) go(pair, now.shoulder ?? false);
    else setPair(pair);
  }, [impacts, enabled, latest, following, go, setPair]);
}

/**
 * The pair the newest attack frames, when its key is unused this turn and it is not the pair already framed - nor the
 * last turn's actor's attack arriving late, which framed the actor whose turn had passed (2026-10-28).
 */
function beatToTake(list, was, now, used, framed, lastTurn) {
  const pair = beatPairOf(list, { seen: was, placed: placedOn(now.tokens) });
  if (!pair || (lastTurn && pair.actorId === lastTurn && pair.actorId !== now.turnActorId)) return null;
  const key = now.turnActorId ? 'turn' : `pair:${pair.actorId}>${pair.targetId}`;
  if (used.has(key) || (framed?.actorId === pair.actorId && framed?.targetId === pair.targetId)) return null;
  used.add(key);
  return pair;
}

/**
 * The follow comes back by itself at the next turn (2026-10-21): a zoom or a turn of the camera keeps the wheel for
 * that turn only. With no order running there is no next turn: the wheel stays the viewer's until Follow.
 */
function useFollowBack(turnActorId, wheelTaken, follow) {
  const last = useRef(turnActorId);
  useEffect(() => {
    if (last.current === turnActorId) return;
    last.current = turnActorId;
    if (turnActorId && wheelTaken) follow();
  }, [turnActorId, wheelTaken, follow]);
}

/**
 * The panel's camera, its gestures taking the wheel from the follow; the camera itself when not following. A RIGHT
 * press takes it only once an orbit is really under way, past the ping's slop (2026-10-21): a ping is a right press
 * too - held still, or choosing a wedge of its wheel, which cancels the orbit - and pinging the map used to switch
 * the follow off for the rest of the session.
 */
function takingTheWheel(cam, following, takeWheel, press) {
  if (!following) {
    press.current = null;
    return cam;
  }
  const grab = (gesture) => (e) => {
    takeWheel();
    gesture(e);
  };
  return { ...cam, ...rightPress(cam, takeWheel, press), onWheel: grab(cam.onWheel), onArrow: grab(cam.onArrow) };
}

/** The press handlers: any other button takes the wheel at once; a right one only once its orbit turns the view. */
function rightPress(cam, takeWheel, press) {
  const onPointerDown = (e) => {
    if (e.button === 2) press.current = { id: e.pointerId, x: e.clientX, y: e.clientY };
    else takeWheel();
    return cam.onPointerDown(e);
  };
  const onPointerMove = (e) => {
    const p = press.current;
    const orbiting = cam.isDragging?.current?.mode === 'orbit';
    if (p && p.id === e.pointerId && orbiting && Math.hypot(e.clientX - p.x, e.clientY - p.y) > SLOP_PX) {
      press.current = null;
      takeWheel();
    }
    return cam.onPointerMove?.(e);
  };
  const onPointerUp = (e) => {
    press.current = null;
    return cam.onPointerUp?.(e);
  };
  return { onPointerDown, onPointerMove, onPointerUp };
}
